import React, { useState, useEffect } from 'react';
import { 
  defaultProducts, 
  defaultVerificationTypes, 
  defaultProductVerificationMappings, 
  defaultLeadCreationQuestions, 
  defaultPositiveVerificationQuestions,
  defaultPositiveRequiredImages,
  defaultNegativeVerificationQuestions,
  defaultNegativeRequiredImages,
} from '../../data/mockData';
import { fetchProducts, fetchCases } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Package, 
  CheckCircle2, 
  Link2, 
  Plus, 
  Trash2, 
  Edit3, 
  Sliders, 
  Download, 
  Upload, 
  FileSpreadsheet, 
  MapPin, 
  Camera, 
  Image as ImageIcon, 
  Send, 
  Mail, 
  Phone, 
  FileText, 
  AlertCircle, 
  Clock, 
  UserCheck, 
  CheckSquare, 
  BarChart3, 
  ShieldCheck, 
  Layers, 
  X,
  FileCheck,
  Zap,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function ProductVerificationManagement() {
  // Tab States
  const [activeTab, setActiveTab] = useState('admin-products');
  
  // Data States
  const [products, setProducts] = useState(defaultProducts);
  const [verificationTypes, setVerificationTypes] = useState(defaultVerificationTypes);
  const [mappings, setMappings] = useState(defaultProductVerificationMappings);
  const [leadCreationQuestions, setLeadCreationQuestions] = useState(defaultLeadCreationQuestions);
  const [positiveQuestions, setPositiveQuestions] = useState(defaultPositiveVerificationQuestions);
  const [negativeQuestions, setNegativeQuestions] = useState(defaultNegativeVerificationQuestions);
  const [positiveRequiredImages, setPositiveRequiredImages] = useState(defaultPositiveRequiredImages);
  const [negativeRequiredImages, setNegativeRequiredImages] = useState(defaultNegativeRequiredImages);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const [prodRes, casesRes] = await Promise.all([
          fetchProducts().catch(() => []),
          fetchCases().catch(() => [])
        ]);
        if (isMounted) {
          if (Array.isArray(prodRes) && prodRes.length > 0) setProducts(prodRes);
          if (Array.isArray(casesRes)) setLeads(casesRes);
        }
      } catch (err) {
        console.error('Failed to load product/case data:', err);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, []);
  
  // Modals / Dialogs States
  const [productDialog, setProductDialog] = useState({ open: false, mode: 'add', data: { id: '', name: '', code: '', status: 'active' } });
  const [verificationTypeDialog, setVerificationTypeDialog] = useState({ open: false, mode: 'add', data: { id: '', name: '', status: 'active' } });
  const [mappingDialog, setMappingDialog] = useState({ open: false, productId: '', mappedTypeIds: [] });
  const [questionDialog, setQuestionDialog] = useState({ open: false, flow: 'lead', mode: 'add', data: { id: '', verificationTypeId: 'VT001', fieldLabel: '', fieldType: 'text', required: false, optionsJson: '', sortOrder: 1 } });
  
  // Dynamic Flow Selected State
  const [selectedProductId, setSelectedProductId] = useState('DP001'); // Credit Card
  const [selectedVerificationTypeId, setSelectedVerificationTypeId] = useState('VT001'); // Residence Verification
  const [selectedLeadId, setSelectedLeadId] = useState('LD001'); // Mock Lead 1
  const [verificationMode, setVerificationMode] = useState(null); // 'positive' or 'negative'

  // Image Upload / Geo-Tag states
  const [capturedImages, setCapturedImages] = useState({});
  const [gpsCoordinates, setGpsCoordinates] = useState({ latitude: '23.0225', longitude: '72.5714', accuracy: '5m', time: new Date().toISOString() });
  const [isManualGeoTagOpen, setIsManualGeoTagOpen] = useState(false);
  const [manualGps, setManualGps] = useState({ latitude: '23.0225', longitude: '72.5714' });
  const [gpsValidated, setGpsValidated] = useState(true);

  // Import / Export Tickboxes
  const [importFields, setImportFields] = useState({
    product: true, verificationType: true, applicantName: true, contactNumber: true,
    address: true, priority: true, pincode: true, city: true, state: true,
    additionalInfo: false, client: false, documentNumber: false, documentOther: false,
    alternateContact: false, companyName: false
  });
  
  const [exportFields, setExportFields] = useState({
    refNo: true, product: true, applicantName: true, contactNumber: true, address: true,
    priority: true, pincode: true, city: true, state: true, additionalInfo: false,
    assignDate: true, assignTo: true, qcBy: false, client: true, businessPartner: false,
    closeDate: false, reassignDate: false, penaltyLevied: false, delayDays: false,
    cost: false, caseStatus: true, createdBy: false, status: true, feRemark: true,
    finalRemark: true, alternateContact: false, companyName: false
  });

  // Dynamic status workflow state per product
  const [productWorkflows, setProductWorkflows] = useState({
    'DP001': ['Created', 'Assigned', 'In Progress', 'Verified', 'QC Pending', 'QC Approved', 'Completed'],
    'default': ['Created', 'Assigned', 'In Progress', 'Verified', 'QC Pending', 'QC Approved', 'Completed']
  });
  const [newWorkflowStep, setNewWorkflowStep] = useState('');

  // Handle Product Add/Edit/Delete
  const handleSaveProduct = () => {
    if (!productDialog.data.name || !productDialog.data.code) {
      toast.error('Product Name and Code are required');
      return;
    }

    if (productDialog.mode === 'add') {
      const newProd = {
        ...productDialog.data,
        id: `DP${String(products.length + 1).padStart(3, '0')}`
      };
      setProducts([...products, newProd]);
      toast.success('Product created successfully');
    } else {
      setProducts(products.map(p => p.id === productDialog.data.id ? productDialog.data : p));
      toast.success('Product updated successfully');
    }
    setProductDialog({ open: false, mode: 'add', data: { id: '', name: '', code: '', status: 'active' } });
  };

  const handleDeleteProduct = (id) => {
    setProducts(products.filter(p => p.id !== id));
    setMappings(mappings.filter(m => m.productId !== id));
    toast.success('Product deleted successfully');
  };

  // Handle Verification Type Add/Edit/Delete
  const handleSaveVerificationType = () => {
    if (!verificationTypeDialog.data.name) {
      toast.error('Verification Type Name is required');
      return;
    }

    if (verificationTypeDialog.mode === 'add') {
      const newType = {
        ...verificationTypeDialog.data,
        id: `VT${String(verificationTypes.length + 1).padStart(3, '0')}`
      };
      setVerificationTypes([...verificationTypes, newType]);
      toast.success('Verification Type created successfully');
    } else {
      setVerificationTypes(verificationTypes.map(v => v.id === verificationTypeDialog.data.id ? verificationTypeDialog.data : v));
      toast.success('Verification Type updated successfully');
    }
    setVerificationTypeDialog({ open: false, mode: 'add', data: { id: '', name: '', status: 'active' } });
  };

  const handleDeleteVerificationType = (id) => {
    setVerificationTypes(verificationTypes.filter(v => v.id !== id));
    setMappings(mappings.filter(m => m.verificationTypeId !== id));
    toast.success('Verification Type deleted successfully');
  };

  // Handle Product-Verification Mapping
  const handleOpenMapping = (productId) => {
    const mapped = mappings.filter(m => m.productId === productId).map(m => m.verificationTypeId);
    setMappingDialog({
      open: true,
      productId,
      mappedTypeIds: mapped
    });
  };

  const handleToggleMapping = (typeId) => {
    if (mappingDialog.mappedTypeIds.includes(typeId)) {
      setMappingDialog({
        ...mappingDialog,
        mappedTypeIds: mappingDialog.mappedTypeIds.filter(id => id !== typeId)
      });
    } else {
      setMappingDialog({
        ...mappingDialog,
        mappedTypeIds: [...mappingDialog.mappedTypeIds, typeId]
      });
    }
  };

  const handleSaveMapping = () => {
    // Delete existing mappings for this product
    const otherMappings = mappings.filter(m => m.productId !== mappingDialog.productId);
    // Create new mappings
    const newMappings = mappingDialog.mappedTypeIds.map((typeId, idx) => ({
      id: `M_NEW_${Date.now()}_${idx}`,
      productId: mappingDialog.productId,
      verificationTypeId: typeId
    }));
    setMappings([...otherMappings, ...newMappings]);
    setMappingDialog({ open: false, productId: '', mappedTypeIds: [] });
    toast.success('Product-Verification mappings updated');
  };

  // Handle Question Creation for Form Engine
  const handleSaveQuestion = () => {
    const qData = questionDialog.data;
    if (!qData.fieldLabel) {
      toast.error('Question Label is required');
      return;
    }

    const targetList = questionDialog.flow === 'lead' ? leadCreationQuestions 
                       : questionDialog.flow === 'positive' ? positiveQuestions 
                       : negativeQuestions;

    const setList = questionDialog.flow === 'lead' ? setLeadCreationQuestions 
                    : questionDialog.flow === 'positive' ? setPositiveQuestions 
                    : setNegativeQuestions;

    if (questionDialog.mode === 'add') {
      const newQ = {
        ...qData,
        id: `Q_${questionDialog.flow.toUpperCase()}_${Date.now()}`
      };
      setList([...targetList, newQ]);
      toast.success('Dynamic question added successfully');
    } else {
      setList(targetList.map(q => q.id === qData.id ? qData : q));
      toast.success('Dynamic question updated successfully');
    }
    setQuestionDialog({ open: false, flow: 'lead', mode: 'add', data: { id: '', verificationTypeId: 'VT001', fieldLabel: '', fieldType: 'text', required: false, optionsJson: '', sortOrder: 1 } });
  };

  const handleDeleteQuestion = (flow, id) => {
    const targetList = flow === 'lead' ? leadCreationQuestions 
                       : flow === 'positive' ? positiveQuestions 
                       : negativeQuestions;
    const setList = flow === 'lead' ? setLeadCreationQuestions 
                    : flow === 'positive' ? setPositiveQuestions 
                    : setNegativeQuestions;

    setList(targetList.filter(q => q.id !== id));
    toast.success('Question deleted successfully');
  };

  // Simulate Geo-Tag Captures
  const simulateCapture = (fieldName) => {
    setCapturedImages({
      ...capturedImages,
      [fieldName]: {
        url: `https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400`,
        gps: `${gpsCoordinates.latitude}, ${gpsCoordinates.longitude}`,
        timestamp: new Date().toLocaleTimeString() + ' ' + new Date().toLocaleDateString(),
        watermark: `ADV CRM • ${new Date().toLocaleDateString()} • Lat:${gpsCoordinates.latitude} Lon:${gpsCoordinates.longitude}`
      }
    });
    toast.success(`${fieldName} captured with auto GPS watermarking!`);
  };

  const applyManualGeoTag = () => {
    setGpsCoordinates({
      ...gpsCoordinates,
      latitude: manualGps.latitude,
      longitude: manualGps.longitude,
      time: new Date().toISOString()
    });
    setGpsValidated(true);
    setIsManualGeoTagOpen(false);
    toast.success('Manual GPS coordinates overridden!');
  };

  // Lead Submission
  const handleLeadSubmit = (e) => {
    e.preventDefault();
    const newLead = {
      id: `LD${String(leads.length + 1).padStart(3, '0')}`,
      refNumber: `LD-2026-${String(leads.length + 1).padStart(4, '0')}`,
      product: products.find(p => p.id === selectedProductId)?.name || 'Credit Card',
      verificationType: verificationTypes.find(v => v.id === selectedVerificationTypeId)?.name || 'Residence Verification',
      applicantName: e.target.applicantName?.value || 'Test Applicant',
      contactNumber: e.target.contactNumber?.value || '+91 99999 88888',
      alternateContact: e.target.alternateContact?.value || '',
      address: e.target.address?.value || '123 Test Villa, SG Road',
      pincode: e.target.pincode?.value || '380054',
      city: e.target.city?.value || 'Ahmedabad',
      state: e.target.state?.value || 'Gujarat',
      priority: e.target.priority?.value || 'medium',
      client: e.target.client?.value || 'Client',
      status: 'assigned',
      assignDate: new Date().toISOString().split('T')[0],
      companyName: e.target.companyName?.value || '',
      comments: e.target.comments?.value || ''
    };
    setLeads([newLead, ...leads]);
    toast.success('Lead created and dynamic questions submitted!');
    e.target.reset();
  };

  // Save Verification Simulator Results
  const saveVerificationData = () => {
    const missingImgs = (verificationMode === 'positive' ? positiveRequiredImages : negativeRequiredImages)
      .filter(img => img.required && !capturedImages[img.label]);
    
    if (missingImgs.length > 0) {
      toast.error(`Please upload required images: ${missingImgs.map(i => i.label).join(', ')}`);
      return;
    }

    toast.success(`Verification Data successfully saved as ${verificationMode.toUpperCase()} Response! EXIF metadata & watermark applied.`);
    // Reset state
    setCapturedImages({});
    setVerificationMode(null);
  };

  // Download Simulation helpers
  const handleDownloadZip = () => {
    toast.info('Downloading watermarked images in a compressed .ZIP file...');
  };
  const handleDownloadPDF = () => {
    toast.info('Generating secure PDF report with EXIF and GPS timestamps...');
  };
  const handleSendEmail = () => {
    toast.success('Verification report successfully emailed to supervisor & client.');
  };
  const handleSendWhatsApp = () => {
    toast.success('Summary report sent via WhatsApp Business channel.');
  };

  // Add Dynamic Workflow Step
  const addWorkflowStep = (prodId) => {
    if (!newWorkflowStep) return;
    const current = productWorkflows[prodId] || productWorkflows['default'];
    setProductWorkflows({
      ...productWorkflows,
      [prodId]: [...current, newWorkflowStep]
    });
    setNewWorkflowStep('');
    toast.success('Workflow state appended');
  };

  const removeWorkflowStep = (prodId, index) => {
    const current = productWorkflows[prodId] || productWorkflows['default'];
    const updated = current.filter((_, idx) => idx !== index);
    setProductWorkflows({
      ...productWorkflows,
      [prodId]: updated
    });
    toast.success('Workflow state removed');
  };

  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="product-verification-module">
      
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-blue-600" />
            Product & Verification Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure dynamic products, verification mapping rules, custom dynamic questions, geo-tagging validation, and workflow engines.
          </p>
        </div>
      </div>

      {/* Main Module Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 gap-1 bg-white p-1 rounded-xl shadow-sm">
        <button 
          onClick={() => setActiveTab('admin-products')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'admin-products' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <Package className="w-4 h-4" />
          Products & Workflows
        </button>
        <button 
          onClick={() => setActiveTab('admin-types')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'admin-types' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <CheckSquare className="w-4 h-4" />
          Verification Types
        </button>
        <button 
          onClick={() => setActiveTab('form-builder')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'form-builder' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <Sliders className="w-4 h-4" />
          Form Builder & Templates
        </button>
        <button 
          onClick={() => setActiveTab('lead-creation')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'lead-creation' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <Plus className="w-4 h-4" />
          Lead Creation Flow
        </button>
        <button 
          onClick={() => setActiveTab('lead-verification')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'lead-verification' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <UserCheck className="w-4 h-4" />
          Verification Flow Engine
        </button>
        <button 
          onClick={() => setActiveTab('analytics')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === 'analytics' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}
        >
          <BarChart3 className="w-4 h-4" />
          Module Reports
        </button>
      </div>

      {/* Tab Contents */}
      <div className="mt-4">
        
        {/* Tab 1: Products & Workflows */}
        {activeTab === 'admin-products' && (
          <div className="space-y-6">
            <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Configure Products & Workflow Timelines</h3>
                  <p className="text-xs text-slate-500">Muted & defaults already established. Map your dynamic verification checklists per product.</p>
                </div>
                <Button 
                  onClick={() => setProductDialog({ open: true, mode: 'add', data: { id: '', name: '', code: '', status: 'active' } })}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </Button>
              </div>

              <ScrollArea className="h-[450px]">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Product Name</th>
                      <th>Product Code</th>
                      <th>Status Badge</th>
                      <th>Mappings</th>
                      <th>Status Workflow Config</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => {
                      const mappedCount = mappings.filter(m => m.productId === product.id).length;
                      const workflow = productWorkflows[product.id] || productWorkflows['default'];
                      
                      return (
                        <tr key={product.id} className="hover:bg-slate-50/50">
                          <td className="font-semibold text-slate-500">{product.id}</td>
                          <td className="font-bold text-slate-900">{product.name}</td>
                          <td className="font-mono text-xs">{product.code}</td>
                          <td>
                            <span className={`status-badge ${product.status === 'active' ? 'success' : 'neutral'}`}>
                              {product.status}
                            </span>
                          </td>
                          <td>
                            <Button 
                              variant="outline" 
                              onClick={() => handleOpenMapping(product.id)}
                              className="text-xs border-slate-200 text-blue-600 gap-1.5"
                            >
                              <Link2 className="w-3.5 h-3.5" />
                              {mappedCount} Verification Mapped
                            </Button>
                          </td>
                          <td>
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-1">
                                {workflow.map((step, idx) => (
                                  <span key={idx} className="bg-slate-100 border text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                                    {step}
                                    <button 
                                      onClick={() => removeWorkflowStep(product.id, idx)}
                                      className="text-red-500 hover:text-red-700 font-bold"
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input 
                                  placeholder="New step..." 
                                  value={newWorkflowStep}
                                  onChange={(e) => setNewWorkflowStep(e.target.value)}
                                  className="h-6 w-28 text-xs"
                                />
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  onClick={() => addWorkflowStep(product.id)}
                                  className="w-6 h-6"
                                >
                                  <Plus className="w-3 h-3 text-blue-600" />
                                </Button>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setProductDialog({ open: true, mode: 'edit', data: product })}
                                className="w-8 h-8 text-slate-500 hover:text-slate-900"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleDeleteProduct(product.id)}
                                className="w-8 h-8 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Tab 2: Verification Types */}
        {activeTab === 'admin-types' && (
          <div className="space-y-6">
            <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Define Verification Checklists</h3>
                  <p className="text-xs text-slate-500">Add, adjust, or retire categories dynamically. Set specific custom constraints.</p>
                </div>
                <Button 
                  onClick={() => setVerificationTypeDialog({ open: true, mode: 'add', data: { id: '', name: '', status: 'active' } })}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Verification Type
                </Button>
              </div>

              <ScrollArea className="h-[450px]">
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Verification Type Name</th>
                      <th>Status Badge</th>
                      <th>Mapped Products count</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verificationTypes.map(type => {
                      const mappingsCount = mappings.filter(m => m.verificationTypeId === type.id).length;
                      
                      return (
                        <tr key={type.id} className="hover:bg-slate-50/50">
                          <td className="font-semibold text-slate-500">{type.id}</td>
                          <td className="font-bold text-slate-900">{type.name}</td>
                          <td>
                            <span className={`status-badge ${type.status === 'active' ? 'success' : 'neutral'}`}>
                              {type.status}
                            </span>
                          </td>
                          <td className="text-slate-600 font-semibold">{mappingsCount} products</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setVerificationTypeDialog({ open: true, mode: 'edit', data: type })}
                                className="w-8 h-8 text-slate-500 hover:text-slate-900"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => handleDeleteVerificationType(type.id)}
                                className="w-8 h-8 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Tab 3: Form Builder & Templates */}
        {activeTab === 'form-builder' && (
          <div className="space-y-6 animate-slide-in-up">
            
            {/* Top configuration options for dynamic form schema selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Product selector to scope form schemas */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl">
                <Label className="text-xs font-bold text-slate-500 uppercase">1. Select Target Product</Label>
                <div className="mt-2">
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Verification Type selector based on product mapping */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl">
                <Label className="text-xs font-bold text-slate-500 uppercase">2. Mapped Verification Types</Label>
                <div className="mt-2">
                  <Select value={selectedVerificationTypeId} onValueChange={setSelectedVerificationTypeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Verification Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {mappings
                        .filter(m => m.productId === selectedProductId)
                        .map(m => {
                          const typeObj = verificationTypes.find(t => t.id === m.verificationTypeId);
                          return typeObj ? (
                            <SelectItem key={typeObj.id} value={typeObj.id}>{typeObj.name}</SelectItem>
                          ) : null;
                        })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action builder menu */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl flex items-center justify-around">
                <Button 
                  onClick={() => setQuestionDialog({ open: true, flow: 'lead', mode: 'add', data: { id: '', verificationTypeId: selectedVerificationTypeId, fieldLabel: '', fieldType: 'text', required: false, optionsJson: '', sortOrder: leadCreationQuestions.length + 1 } })}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Lead Field
                </Button>
                <Button 
                  onClick={() => setQuestionDialog({ open: true, flow: 'positive', mode: 'add', data: { id: '', verificationTypeId: selectedVerificationTypeId, fieldLabel: '', fieldType: 'text', required: false, optionsJson: '', sortOrder: positiveQuestions.length + 1 } })}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Positive Verification
                </Button>
              </div>

            </div>

            {/* Configured Columns, Import templates, Dynamic Builder Details */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Import & Export Customization Panels */}
              <div className="space-y-6">
                
                {/* Import Template customization */}
                <div className="crm-card p-5 bg-white shadow-sm rounded-xl">
                  <h4 className="text-sm font-bold text-slate-800 border-b pb-2 mb-3 flex items-center gap-1.5">
                    <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                    Import Template Fields
                  </h4>
                  <p className="text-[11px] text-slate-500 mb-4">Choose which custom fields are dynamically imported via Product XLS templates.</p>
                  <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-2 scrollbar-thin">
                    {Object.keys(importFields).map(field => (
                      <div key={field} className="flex items-center justify-between border-b border-slate-50 pb-1.5">
                        <span className="text-xs font-semibold capitalize text-slate-700">
                          {field.replace(/([A-Z])/g, ' $1')}
                        </span>
                        <Switch 
                          checked={importFields[field]} 
                          onCheckedChange={(checked) => setImportFields({ ...importFields, [field]: checked })}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Column Customization */}
                <div className="crm-card p-5 bg-white shadow-sm rounded-xl">
                  <h4 className="text-sm font-bold text-slate-800 border-b pb-2 mb-3 flex items-center gap-1.5">
                    <Download className="w-4 h-4 text-green-600" />
                    Export Column Schema
                  </h4>
                  <p className="text-[11px] text-slate-500 mb-4">Set which columns are visible in custom dynamic spreadsheets outputted for vendors.</p>
                  <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-2 scrollbar-thin">
                    {Object.keys(exportFields).map(field => (
                      <div key={field} className="flex items-center justify-between border-b border-slate-50 pb-1.5">
                        <span className="text-xs font-semibold capitalize text-slate-700">
                          {field.replace(/([A-Z])/g, ' $1')}
                        </span>
                        <Checkbox 
                          checked={exportFields[field]} 
                          onCheckedChange={(checked) => setExportFields({ ...exportFields, [field]: !!checked })}
                        />
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* Dynamic Field Display & Editing (Core Form Schema Engine Viewer) */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Lead Creation dynamically configured questions list */}
                <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
                  <h4 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-1.5">
                    <Sliders className="w-5 h-5 text-blue-600" />
                    Dynamic Form Fields: Lead Creation
                  </h4>
                  <div className="space-y-3">
                    {leadCreationQuestions
                      .filter(q => q.verificationTypeId === selectedVerificationTypeId)
                      .map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3.5 border border-slate-100 rounded-lg hover:bg-slate-50 bg-slate-50/20">
                          <div className="flex items-center gap-3">
                            <span className="bg-blue-100 text-blue-800 text-[10px] font-extrabold px-2 py-0.5 rounded uppercase">
                              {q.fieldType}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{q.fieldLabel}</span>
                            {q.required && <span className="text-red-500 font-bold">*</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setQuestionDialog({ open: true, flow: 'lead', mode: 'edit', data: q })}
                              className="w-7 h-7"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteQuestion('lead', q.id)}
                              className="w-7 h-7 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Positive Verification Form dynamic questions */}
                <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
                  <h4 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-1.5">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Dynamic Form Fields: Positive Verification
                  </h4>
                  
                  {/* Required Images for Positive verification */}
                  <div className="mb-4 bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs font-bold text-slate-600 uppercase">Positive Required Image Rules</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {positiveRequiredImages.map((img, idx) => (
                        <div key={idx} className="bg-white border rounded p-2 text-xs flex items-center justify-between">
                          <span className="font-semibold text-slate-700">{img.label}</span>
                          <span className="text-[9px] font-bold uppercase text-slate-400">
                            {img.geoTag ? 'Geo-Tag' : 'Normal'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {positiveQuestions
                      .filter(q => q.verificationTypeId === selectedVerificationTypeId)
                      .map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3.5 border border-slate-100 rounded-lg hover:bg-slate-50 bg-slate-50/20">
                          <div className="flex items-center gap-3">
                            <span className="bg-green-100 text-green-800 text-[10px] font-extrabold px-2 py-0.5 rounded uppercase">
                              {q.fieldType}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{q.fieldLabel}</span>
                            {q.required && <span className="text-red-500 font-bold">*</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setQuestionDialog({ open: true, flow: 'positive', mode: 'edit', data: q })}
                              className="w-7 h-7"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteQuestion('positive', q.id)}
                              className="w-7 h-7 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Negative Verification Form dynamic questions */}
                <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
                  <h4 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-1.5">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    Dynamic Form Fields: Negative Verification
                  </h4>
                  
                  {/* Required Images for Negative verification */}
                  <div className="mb-4 bg-slate-50 p-4 rounded-lg">
                    <Label className="text-xs font-bold text-slate-600 uppercase">Negative Required Image Rules</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {negativeRequiredImages.map((img, idx) => (
                        <div key={idx} className="bg-white border rounded p-2 text-xs flex items-center justify-between">
                          <span className="font-semibold text-slate-700">{img.label}</span>
                          <span className="text-[9px] font-bold uppercase text-slate-400">
                            {img.geoTag ? 'Geo-Tag' : 'Normal'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {negativeQuestions
                      .filter(q => q.verificationTypeId === selectedVerificationTypeId)
                      .map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3.5 border border-slate-100 rounded-lg hover:bg-slate-50 bg-slate-50/20">
                          <div className="flex items-center gap-3">
                            <span className="bg-red-100 text-red-800 text-[10px] font-extrabold px-2 py-0.5 rounded uppercase">
                              {q.fieldType}
                            </span>
                            <span className="text-sm font-semibold text-slate-800">{q.fieldLabel}</span>
                            {q.required && <span className="text-red-500 font-bold">*</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setQuestionDialog({ open: true, flow: 'negative', mode: 'edit', data: q })}
                              className="w-7 h-7"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteQuestion('negative', q.id)}
                              className="w-7 h-7 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

              </div>

            </div>

          </div>
        )}

        {/* Tab 4: Lead Creation Flow */}
        {activeTab === 'lead-creation' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-in-up">
            
            {/* Step Selection & Dynamic Form generation */}
            <div className="lg:col-span-2 crm-card p-6 bg-white shadow-sm rounded-xl space-y-6">
              
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-slate-900">Step-by-Step Dynamic Lead Creation</h3>
                <p className="text-xs text-slate-500">Follow the wizard flow to dynamically generate specific fields.</p>
              </div>

              {/* Wizard Steps */}
              <form onSubmit={handleLeadSubmit} className="space-y-6">
                
                {/* Step 1: Select Product */}
                <div className="space-y-2">
                  <Label className="text-xs font-extrabold text-slate-500 uppercase">Step 1: Choose Product Classification</Label>
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 2: Select Verification Type */}
                <div className="space-y-2">
                  <Label className="text-xs font-extrabold text-slate-500 uppercase">Step 2: Choose Verification Type Checklist</Label>
                  <Select value={selectedVerificationTypeId} onValueChange={setSelectedVerificationTypeId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose verification checklist..." />
                    </SelectTrigger>
                    <SelectContent>
                      {mappings
                        .filter(m => m.productId === selectedProductId)
                        .map(m => {
                          const typeObj = verificationTypes.find(t => t.id === m.verificationTypeId);
                          return typeObj ? (
                            <SelectItem key={typeObj.id} value={typeObj.id}>{typeObj.name}</SelectItem>
                          ) : null;
                        })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Step 3: Dynamic Inputs based on VT001 custom form mapping schema */}
                <div className="space-y-4 border-t pt-4">
                  <Label className="text-xs font-extrabold text-slate-500 uppercase block mb-3">
                    Step 3: Dynamically Generated Fields (Residence Verification Schema)
                  </Label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {leadCreationQuestions
                      .filter(q => q.verificationTypeId === selectedVerificationTypeId)
                      .map((q) => {
                        const name = q.fieldLabel.replace(/\s+/g, '');
                        return (
                          <div key={q.id} className="space-y-1">
                            <Label className="text-xs font-semibold text-slate-700">
                              {q.fieldLabel} {q.required && <span className="text-red-500 font-bold">*</span>}
                            </Label>
                            {q.fieldType === 'textarea' ? (
                              <textarea 
                                name={name} 
                                required={q.required}
                                className="w-full text-xs p-2 rounded-lg border border-slate-200 focus-visible:ring-1 bg-slate-50/50" 
                                rows={3}
                              />
                            ) : (
                              <Input 
                                type={q.fieldType === 'number' ? 'number' : 'text'}
                                name={name}
                                required={q.required}
                                className="bg-slate-50/50 border-slate-200"
                              />
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Step 4: Submit Lead */}
                <div className="border-t pt-4 flex justify-end gap-3">
                  <Button type="button" variant="outline" className="border-slate-200">Reset</Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2">
                    <Send className="w-4 h-4" />
                    Submit Dynamic Lead Case
                  </Button>
                </div>

              </form>

            </div>

            {/* Step 5: Bulk Import Option */}
            <div className="crm-card p-6 bg-white shadow-sm rounded-xl space-y-6">
              
              <div className="border-b pb-4">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-1.5">
                  <Upload className="w-5 h-5 text-blue-600" />
                  Step 5: Bulk XLSX Import
                </h3>
                <p className="text-xs text-slate-500">Upload bulk batches matching dynamic criteria.</p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-slate-600">Product Template Type</Label>
                  <Select defaultValue="DP001">
                    <SelectTrigger>
                      <SelectValue placeholder="Product XLS Mapping Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50/50 hover:bg-slate-50 cursor-pointer">
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-blue-500 mb-3" />
                  <span className="block text-xs font-bold text-slate-700">Drag & Drop Batch Template</span>
                  <span className="block text-[10px] text-slate-400 mt-1">XLSX or CSV format supported</span>
                </div>

                <Button 
                  onClick={() => toast.success('Mock File Uploaded! 23 cases imported under dynamic residence mapping.')} 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold gap-2 text-xs"
                >
                  <Upload className="w-4 h-4" />
                  Process Bulk Import Batch
                </Button>
              </div>

            </div>

          </div>
        )}

        {/* Tab 5: Lead Verification Flow Engine */}
        {activeTab === 'lead-verification' && (
          <div className="space-y-6 animate-slide-in-up">
            
            {/* Simulation controls */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Select active simulated Lead */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl">
                <Label className="text-xs font-extrabold text-slate-500 uppercase">Simulated Active Assigned Case</Label>
                <div className="mt-2">
                  <Select value={selectedLeadId} onValueChange={(val) => {
                    setSelectedLeadId(val);
                    setVerificationMode(null);
                    setCapturedImages({});
                  }}>
                    <SelectTrigger className="w-full font-bold">
                      <SelectValue placeholder="Select Lead" />
                    </SelectTrigger>
                    <SelectContent>
                      {leads.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.applicantName} ({l.product})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Watermark / GPS validations simulation box */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl flex items-center justify-between">
                <div>
                  <span className="block text-xs font-extrabold text-slate-500 uppercase">GPS Auto-Tagger Validation</span>
                  <span className="block text-sm font-bold text-slate-900 mt-1 flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-green-600" />
                    {gpsCoordinates.latitude}, {gpsCoordinates.longitude} (ACC: {gpsCoordinates.accuracy})
                  </span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">EXIF Timestamp & Watermark: Enabled</span>
                </div>
                <div className="flex flex-col gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => setIsManualGeoTagOpen(true)}
                    className="text-xs border-slate-200 font-semibold gap-1 text-slate-700 bg-slate-50"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Manual Geo Tag
                  </Button>
                </div>
              </div>

              {/* Quick actions buttons panel */}
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl flex items-center justify-around gap-2">
                <Button onClick={handleDownloadZip} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5">
                  <Download className="w-3.5 h-3.5" />
                  Images ZIP
                </Button>
                <Button onClick={handleDownloadPDF} size="sm" variant="outline" className="border-slate-200 text-xs gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  PDF Report
                </Button>
                <div className="flex items-center gap-1">
                  <Button onClick={handleSendEmail} size="icon" variant="ghost" className="w-8 h-8 text-slate-500 hover:text-slate-900">
                    <Mail className="w-4 h-4" />
                  </Button>
                  <Button onClick={handleSendWhatsApp} size="icon" variant="ghost" className="w-8 h-8 text-slate-500 hover:text-slate-900">
                    <Phone className="w-4 h-4" />
                  </Button>
                </div>
              </div>

            </div>

            {/* Simulated Case Lead Details & Verification workflow */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Column: Simulated Case Details Card */}
              {(() => {
                const leadObj = leads.find(l => l.id === selectedLeadId);
                if (!leadObj) return null;
                
                return (
                  <div className="crm-card p-6 bg-white shadow-sm rounded-xl space-y-5">
                    <div className="border-b pb-3 flex items-center justify-between">
                      <div>
                        <span className="bg-blue-50 border border-blue-200 text-blue-600 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase">
                          {leadObj.priority} priority
                        </span>
                        <h4 className="text-base font-bold text-slate-900 mt-2">{leadObj.applicantName}</h4>
                        <span className="text-[10px] font-semibold text-slate-400">{leadObj.refNumber}</span>
                      </div>
                      <span className="text-xs bg-slate-100 text-slate-800 px-2 py-1 rounded font-bold">
                        {leadObj.status}
                      </span>
                    </div>

                    <div className="space-y-3.5 text-xs">
                      <div>
                        <span className="block text-slate-400 font-bold uppercase text-[9px]">Mapped Product</span>
                        <span className="font-bold text-slate-900">{leadObj.product}</span>
                      </div>
                      <div>
                        <span className="block text-slate-400 font-bold uppercase text-[9px]">Verification Action</span>
                        <span className="font-bold text-slate-900">{leadObj.verificationType}</span>
                      </div>
                      <div>
                        <span className="block text-slate-400 font-bold uppercase text-[9px]">Contact Details</span>
                        <span className="font-semibold text-slate-800">{leadObj.contactNumber}</span>
                        {leadObj.alternateContact && <span className="block text-slate-500 font-semibold">{leadObj.alternateContact} (Alt)</span>}
                      </div>
                      <div>
                        <span className="block text-slate-400 font-bold uppercase text-[9px]">Premises Address</span>
                        <span className="font-semibold text-slate-800">{leadObj.address}</span>
                        <span className="block text-slate-500 font-bold mt-0.5">{leadObj.city}, {leadObj.state} - {leadObj.pincode}</span>
                      </div>
                      {leadObj.companyName && (
                        <div>
                          <span className="block text-slate-400 font-bold uppercase text-[9px]">Client / Recruited Company</span>
                          <span className="font-semibold text-slate-800">{leadObj.companyName}</span>
                        </div>
                      )}
                      <div>
                        <span className="block text-slate-400 font-bold uppercase text-[9px]">Internal Instructions</span>
                        <span className="block bg-slate-50 p-2.5 rounded border border-slate-100 text-[11px] text-slate-600 font-semibold">
                          {leadObj.comments}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Right Column: Step 2: Show buttons & dynamic question inputs */}
              <div className="lg:col-span-2 crm-card p-6 bg-white shadow-sm rounded-xl space-y-6">
                
                <div className="border-b pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Verification Response Engine</h3>
                    <p className="text-xs text-slate-500">Pick verification outcome to trigger dynamic workflow form.</p>
                  </div>
                  
                  {/* Positive / Negative response buttons triggers */}
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => {
                        setVerificationMode('positive');
                        setCapturedImages({});
                      }}
                      className={`font-bold px-4 text-xs gap-1.5 ${verificationMode === 'positive' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Positive Outcome Form
                    </Button>
                    <Button 
                      onClick={() => {
                        setVerificationMode('negative');
                        setCapturedImages({});
                      }}
                      className={`font-bold px-4 text-xs gap-1.5 ${verificationMode === 'negative' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'}`}
                    >
                      <AlertCircle className="w-4 h-4" />
                      Negative Outcome Form
                    </Button>
                  </div>
                </div>

                {/* Simulated dynamic forms renderer */}
                {!verificationMode ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <Zap className="w-12 h-12 text-slate-300 mb-3 animate-bounce" />
                    <p className="text-sm font-semibold">Please select POSITIVE or NEGATIVE outcome to begin inputting.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    
                    {/* Render fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(verificationMode === 'positive' ? positiveQuestions : negativeQuestions)
                        .filter(q => q.verificationTypeId === 'VT001')
                        .map((q) => {
                          const options = q.optionsJson ? JSON.parse(q.optionsJson) : [];
                          return (
                            <div key={q.id} className="space-y-1.5">
                              <Label className="text-xs font-semibold text-slate-700">
                                {q.fieldLabel} {q.required && <span className="text-red-500">*</span>}
                              </Label>

                              {q.fieldType === 'dropdown' ? (
                                <Select>
                                  <SelectTrigger className="bg-slate-50/50 border-slate-200">
                                    <SelectValue placeholder="Select dynamic option..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {options.map((opt, idx) => (
                                      <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : q.fieldType === 'textarea' ? (
                                <textarea 
                                  className="w-full text-xs p-2 rounded-lg border border-slate-200 focus-visible:ring-1 bg-slate-50/50" 
                                  rows={3}
                                />
                              ) : (
                                <Input 
                                  type={q.fieldType === 'date' ? 'date' : q.fieldType === 'number' ? 'number' : 'text'}
                                  className="bg-slate-50/50 border-slate-200"
                                />
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {/* Image uploads validation engine */}
                    <div className="border-t pt-4 space-y-4">
                      <Label className="text-xs font-bold text-slate-600 uppercase block">
                        Required Watermarked Evidence Captures
                      </Label>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(verificationMode === 'positive' ? positiveRequiredImages : negativeRequiredImages)
                          .map((img, idx) => {
                            const isCaptured = !!capturedImages[img.label];
                            const capData = capturedImages[img.label];

                            return (
                              <div key={idx} className="p-4 border rounded-xl bg-slate-50 flex items-center justify-between gap-4">
                                <div className="space-y-1">
                                  <span className="text-xs font-bold text-slate-800 flex items-center gap-1">
                                    {img.label}
                                    {img.required && <span className="text-red-500">*</span>}
                                  </span>
                                  <span className="block text-[10px] text-slate-400">
                                    {img.geoTag ? 'Coordinates Watermark mandated' : 'Standard snapshot'}
                                  </span>
                                  {isCaptured && (
                                    <span className="block text-[9px] text-green-600 font-semibold bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
                                      GPS: {capData.gps} • {capData.timestamp}
                                    </span>
                                  )}
                                </div>

                                <div className="flex-shrink-0">
                                  {isCaptured ? (
                                    <div className="relative group">
                                      <img 
                                        src={capData.url} 
                                        alt={img.label} 
                                        className="w-12 h-12 object-cover rounded-lg border border-slate-300"
                                      />
                                      {/* Manual Geo Tagger button on the image */}
                                      <button 
                                        onClick={() => setIsManualGeoTagOpen(true)}
                                        className="absolute -top-1.5 -right-1.5 bg-blue-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Manually Geo Tag this image"
                                      >
                                        <MapPin className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      onClick={() => simulateCapture(img.label)}
                                      className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs gap-1"
                                    >
                                      <Camera className="w-3.5 h-3.5" />
                                      Capture
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* Submit Verification outcome */}
                    <div className="border-t pt-4 flex justify-end gap-3">
                      <Button variant="outline" className="border-slate-200">Cancel</Button>
                      <Button 
                        onClick={saveVerificationData} 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold gap-1.5 text-xs px-6"
                      >
                        <FileCheck className="w-4 h-4" />
                        Save Verification Evidence
                      </Button>
                    </div>

                  </div>
                )}

              </div>

            </div>

          </div>
        )}

        {/* Tab 6: Analytics & Module Reports */}
        {activeTab === 'analytics' && (
          <div className="space-y-6 animate-slide-in-up">
            
            {/* Quick stats grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl kpi-card">
                <div className="kpi-icon bg-blue-50 text-blue-600">
                  <Sliders className="w-5 h-5" />
                </div>
                <div className="kpi-value mt-2">1,847</div>
                <div className="kpi-label">Active mapped Cases</div>
              </div>
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl kpi-card">
                <div className="kpi-icon bg-yellow-50 text-yellow-600">
                  <Clock className="w-5 h-5" />
                </div>
                <div className="kpi-value mt-2">234</div>
                <div className="kpi-label">Pending Verification Cases</div>
              </div>
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl kpi-card">
                <div className="kpi-icon bg-green-50 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <div className="kpi-value mt-2">86.4%</div>
                <div className="kpi-label">Positive Outcomes Ratio</div>
              </div>
              <div className="crm-card p-5 bg-white shadow-sm rounded-xl kpi-card">
                <div className="kpi-icon bg-purple-50 text-purple-600">
                  <Zap className="w-5 h-5" />
                </div>
                <div className="kpi-value mt-2">98.2%</div>
                <div className="kpi-label">FE Auto-GPS Productivity</div>
              </div>
            </div>

            {/* Delay Reports & Product wise breakdown lists */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Product Wise Performance breakdown */}
              <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
                <h4 className="text-base font-bold text-slate-900 border-b pb-3 mb-4 flex items-center gap-1.5">
                  <Package className="w-5 h-5 text-blue-600" />
                  Product-Wise Case Distribution
                </h4>
                <div className="space-y-4">
                  {[
                    { name: 'Credit Card', count: 650, percentage: 35, color: 'bg-blue-600' },
                    { name: 'Personal Loan', count: 420, percentage: 22, color: 'bg-green-600' },
                    { name: 'Home Loan', count: 380, percentage: 20, color: 'bg-purple-600' },
                    { name: 'Gold Loan', count: 220, percentage: 12, color: 'bg-yellow-600' },
                    { name: 'Business Loan', count: 177, percentage: 11, color: 'bg-red-600' },
                  ].map((item, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs font-semibold text-slate-700">
                        <span>{item.name}</span>
                        <span>{item.count} cases ({item.percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div className={`h-full ${item.color}`} style={{ width: `${item.percentage}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Delay Tracking report log */}
              <div className="crm-card p-6 bg-white shadow-sm rounded-xl">
                <h4 className="text-base font-bold text-slate-900 border-b pb-3 mb-4 flex items-center gap-1.5">
                  <Clock className="w-5 h-5 text-red-600" />
                  Real-time SLA Delay Tracking
                </h4>
                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
                  {[
                    { id: 'CASE098', applicant: 'Priyank Jain', delay: '3 Days Delay', penalty: '₹150 Penalty', status: 'In QC' },
                    { id: 'CASE104', applicant: 'Mukesh Ambani', delay: '5 Days Delay', penalty: '₹250 Penalty', status: 'Assigned' },
                    { id: 'CASE231', applicant: 'Gaurav Patel', delay: '1 Day Delay', penalty: '₹50 Penalty', status: 'In Progress' },
                    { id: 'CASE412', applicant: 'Alok Nath', delay: '7 Days Delay', penalty: '₹350 Penalty', status: 'Revisit' },
                  ].map((delayLog, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50/50 hover:bg-slate-50">
                      <div>
                        <span className="text-xs font-bold text-slate-800">{delayLog.applicant}</span>
                        <span className="block text-[10px] text-slate-400 font-semibold">{delayLog.id} • SLA Status: {delayLog.status}</span>
                      </div>
                      <div className="text-right">
                        <span className="block text-xs font-extrabold text-red-600">{delayLog.delay}</span>
                        <span className="block text-[10px] font-bold text-slate-500 bg-yellow-100 px-2 py-0.5 rounded mt-0.5">
                          {delayLog.penalty}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>

          </div>
        )}

      </div>

      {/* --- Dialog Modals --- */}

      {/* 1. Product Add / Edit Dialog */}
      <Dialog open={productDialog.open} onOpenChange={(open) => setProductDialog({ ...productDialog, open })}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold">
              {productDialog.mode === 'add' ? 'Add Dynamic CRM Product' : 'Edit CRM Product'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Set product parameters.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Product Name</Label>
              <Input 
                value={productDialog.data.name}
                onChange={(e) => setProductDialog({ ...productDialog, data: { ...productDialog.data, name: e.target.value } })}
                className="bg-slate-50/50 border-slate-200"
                placeholder="Credit Card"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Product Code</Label>
              <Input 
                value={productDialog.data.code}
                onChange={(e) => setProductDialog({ ...productDialog, data: { ...productDialog.data, code: e.target.value } })}
                className="bg-slate-50/50 border-slate-200"
                placeholder="CC"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Active Status</Label>
              <Select 
                value={productDialog.data.status}
                onValueChange={(status) => setProductDialog({ ...productDialog, data: { ...productDialog.data, status } })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialog({ ...productDialog, open: false })}>Cancel</Button>
            <Button onClick={handleSaveProduct} className="bg-blue-600 hover:bg-blue-700 text-white">Save Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2. Verification Type Add / Edit Dialog */}
      <Dialog open={verificationTypeDialog.open} onOpenChange={(open) => setVerificationTypeDialog({ ...verificationTypeDialog, open })}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold">
              {verificationTypeDialog.mode === 'add' ? 'Add Verification Type' : 'Edit Verification Type'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Define the verification checklist standard.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Verification Type Name</Label>
              <Input 
                value={verificationTypeDialog.data.name}
                onChange={(e) => setVerificationTypeDialog({ ...verificationTypeDialog, data: { ...verificationTypeDialog.data, name: e.target.value } })}
                className="bg-slate-50/50 border-slate-200"
                placeholder="Residence Verification"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Active Status</Label>
              <Select 
                value={verificationTypeDialog.data.status}
                onValueChange={(status) => setVerificationTypeDialog({ ...verificationTypeDialog, data: { ...verificationTypeDialog.data, status } })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerificationTypeDialog({ ...verificationTypeDialog, open: false })}>Cancel</Button>
            <Button onClick={handleSaveVerificationType} className="bg-blue-600 hover:bg-blue-700 text-white">Save Type</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Product Verification Mapping Dialog */}
      <Dialog open={mappingDialog.open} onOpenChange={(open) => setMappingDialog({ ...mappingDialog, open })}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold">
              Map Verification Types to Product
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              A product can request multiple dynamic verification actions concurrently.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="py-4 h-[250px] pr-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {verificationTypes.map(type => {
                const isChecked = mappingDialog.mappedTypeIds.includes(type.id);
                return (
                  <div 
                    key={type.id} 
                    onClick={() => handleToggleMapping(type.id)}
                    className={`p-3 border rounded-lg cursor-pointer flex items-center justify-between hover:bg-slate-50 ${isChecked ? 'border-blue-500 bg-blue-50/30' : 'border-slate-100 bg-white'}`}
                  >
                    <span className="text-xs font-bold text-slate-700">{type.name}</span>
                    <Checkbox checked={isChecked} onCheckedChange={() => {}} />
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingDialog({ open: false, productId: '', mappedTypeIds: [] })}>Cancel</Button>
            <Button onClick={handleSaveMapping} className="bg-blue-600 hover:bg-blue-700 text-white">Save Mappings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4. Dynamic Field Builder Add / Edit Dialog */}
      <Dialog open={questionDialog.open} onOpenChange={(open) => setQuestionDialog({ ...questionDialog, open })}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold capitalize">
              {questionDialog.mode === 'add' ? `Add ${questionDialog.flow} Form Field` : `Edit ${questionDialog.flow} Form Field`}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Configure dynamic parameters.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Field Label (Question Text)</Label>
              <Input 
                value={questionDialog.data.fieldLabel}
                onChange={(e) => setQuestionDialog({ ...questionDialog, data: { ...questionDialog.data, fieldLabel: e.target.value } })}
                className="bg-slate-50/50 border-slate-200"
                placeholder="e.g. Landmark"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Field Type</Label>
              <Select 
                value={questionDialog.data.fieldType}
                onValueChange={(fieldType) => setQuestionDialog({ ...questionDialog, data: { ...questionDialog.data, fieldType } })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text Input</SelectItem>
                  <SelectItem value="number">Number Input</SelectItem>
                  <SelectItem value="dropdown">Dropdown Options</SelectItem>
                  <SelectItem value="date">Date Picker</SelectItem>
                  <SelectItem value="textarea">Paragraph TextArea</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {questionDialog.data.fieldType === 'dropdown' && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Dropdown Options (JSON Array string format)</Label>
                <Input 
                  value={questionDialog.data.optionsJson}
                  onChange={(e) => setQuestionDialog({ ...questionDialog, data: { ...questionDialog.data, optionsJson: e.target.value } })}
                  className="bg-slate-50/50 border-slate-200 font-mono text-xs"
                  placeholder='["Owned", "Rented", "Bungalow"]'
                />
              </div>
            )}
            <div className="flex items-center justify-between border-t border-b py-2">
              <Label className="text-xs text-slate-600">Required Field Validation</Label>
              <Switch 
                checked={questionDialog.data.required}
                onCheckedChange={(required) => setQuestionDialog({ ...questionDialog, data: { ...questionDialog.data, required } })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Sort Order Hierarchy</Label>
              <Input 
                type="number"
                value={questionDialog.data.sortOrder}
                onChange={(e) => setQuestionDialog({ ...questionDialog, data: { ...questionDialog.data, sortOrder: Number(e.target.value) } })}
                className="bg-slate-50/50 border-slate-200"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuestionDialog({ ...questionDialog, open: false })}>Cancel</Button>
            <Button onClick={handleSaveQuestion} className="bg-blue-600 hover:bg-blue-700 text-white">Save Field</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 5. Manual Geo Tag override Dialog */}
      <Dialog open={isManualGeoTagOpen} onOpenChange={setIsManualGeoTagOpen}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900 font-extrabold flex items-center gap-1">
              <MapPin className="w-5 h-5 text-blue-600" />
              Manual Geo Tag Override Tab
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Manually place GPS coordinates if auto-GPS fails or is out of range.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Override Latitude Coordinate</Label>
              <Input 
                value={manualGps.latitude}
                onChange={(e) => setManualGps({ ...manualGps, latitude: e.target.value })}
                className="bg-slate-50/50 border-slate-200 font-semibold"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Override Longitude Coordinate</Label>
              <Input 
                value={manualGps.longitude}
                onChange={(e) => setManualGps({ ...manualGps, longitude: e.target.value })}
                className="bg-slate-50/50 border-slate-200 font-semibold"
              />
            </div>
            <div className="bg-blue-50 border p-3 rounded-lg text-[10px] text-blue-700 leading-relaxed font-semibold">
              Note: Watermarking metadata will apply this override timestamp and manual geo-tag coordinates onto all upcoming evidence snapshots.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualGeoTagOpen(false)}>Cancel</Button>
            <Button onClick={applyManualGeoTag} className="bg-blue-600 hover:bg-blue-700 text-white">Override GPS</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
